import { Injectable } from "@angular/core";
import { Share } from "@capacitor/share";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { HttpClient } from "@angular/common/http";
import write_blob from "capacitor-blob-writer";
import { lastValueFrom } from "rxjs";
import { Capacitor } from "@capacitor/core";
import { LogService } from "./log.service";
import { handlePromise } from "../../utils";
import { BaseService } from "./base.service";
import { ObjectURLService } from "./object.url.service";

@Injectable({
  providedIn: "root",
})
export class FileService extends BaseService {
  constructor(
    private http: HttpClient,
    private objectURLService: ObjectURLService,
    logService: LogService,
  ) {
    super(logService, "FileService");
  }

  public getFileName(path: string, includeExtension: boolean = true): string {
    // Check for empty or invalid path
    if (!path || typeof path !== "string") {
      return "";
    }
    // Extract the file name (last part after the last slash)
    const splitPath = path.split("/");
    const name = splitPath.pop() || ""; // Safe extraction of the last part
    // If the name contains no ".", return as is
    if (!name.includes(".")) {
      return name;
    }
    // Return full name if includeExtension is true, otherwise return the name without extension
    return includeExtension ? name : name.substring(0, name.lastIndexOf("."));
  }

  public getFileExtension(path: string): string {
    // Check for empty or invalid path
    if (!path || typeof path !== "string") {
      return "";
    }
    // Extract the file name (last part after the last slash)
    const splitPath = path.split("/");
    const name = splitPath.pop() || ""; // Safe extraction of the last part
    // Check if the file name contains a "." (indicating an extension)
    const lastDotIndex = name.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === name.length - 1) {
      // No "." or the dot is the last character (meaning no extension)
      return "";
    }
    // Extract and return the extension
    return name.substring(lastDotIndex + 1);
  }

  public convertObjectUrlToFile(data: string | Blob) {
    if (data instanceof Blob) {
      return data;
    }
    return lastValueFrom(
      this.http.get(data, {
        responseType: "blob" as "json",
      }),
    );
  }

  public async serveAsset(path: string) {
    if (this.objectURLService.exists(path)) {
      return this.objectURLService.get(path);
    }
    const blob: any = await this.convertObjectUrlToFile(path);
    const url = this.objectURLService.create(path, blob);
    return Capacitor.convertFileSrc(url);
  }

  public convertBlobUrlToBase64(blobUrl: any): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.onload = () => {
        const reader = new FileReader();
        reader.readAsDataURL(xhr.response);
        reader.onloadend = () => {
          const base64data = reader.result;
          resolve(base64data as string);
        };
        reader.onerror = reject;
      };
      xhr.open("GET", blobUrl);
      xhr.responseType = "blob";
      xhr.send();
    });
  }

  public getCache(path: string = "") {
    return Filesystem.readdir({
      path,
      directory: Directory.Cache,
    });
  }

  public async removeFolder(path: string): Promise<boolean> {
    const [err] = await handlePromise(
      Filesystem.rmdir({
        path,
        directory: Directory.Cache,
        recursive: true,
      }),
    );
    if (!err) {
      return true;
    }
    return false;
  }

  public base64StringToBlobConversion(
    data: string | Blob,
    contentType: string = "",
    sliceSize: number = 512,
  ): Blob {
    if (data instanceof Blob) {
      return new Blob([data], { type: contentType });
    }
    const byteCharacters = atob(data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i += 1) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  public removeFiles(
    filePaths: string[],
    directory: Directory = Directory.Data,
  ) {
    return Promise.all(
      filePaths.map((path) => this.removeFile(path, directory)),
    );
  }

  public async removeFile(path: string, directory: Directory = Directory.Data) {
    const [err] = await handlePromise(
      Filesystem.deleteFile({
        path,
        directory,
      }),
    );
    if (err) {
      this.logger.error({
        functionName: "removeFile",
        path,
        directory,
        error: err,
      });
      return false;
    }
    this.objectURLService.remove(path);
    return true;
  }

  public stopServingFile(path: string) {
    if (this.objectURLService.exists(path)) {
      this.objectURLService.remove(path);
    }
  }

  public async checkIfFilesExist(
    fileNames: string[],
    directory: any = Directory.Data,
  ): Promise<{ [fileName: string]: boolean }> {
    try {
      const result = await Filesystem.readdir({
        path: "",
        directory,
      });
      return fileNames.reduce((acc, fileName) => {
        const { files } = result;
        if (fileName.endsWith("*")) {
          const partialName = fileName.slice(0, -1);
          const filtered = files.filter((file) =>
            file.name.startsWith(partialName),
          );
          const filteredMap = filtered.reduce((acc2, file) => {
            return {
              ...acc2,
              [file.name]: true,
            };
          }, {});
          if (filtered.length) {
            return {
              ...acc,
              ...filteredMap,
            };
          }
          return acc;
        }
        return {
          ...acc,
          [fileName]: files.some((file) => file.name === fileName),
        };
      }, {});
    } catch (err) {
      this.logger.error({
        functionName: "checkIfFilesExist",
        fileNames,
        error: err,
      });
      return fileNames.reduce(
        (acc, fileName) => ({ ...acc, [fileName]: false }),
        {},
      );
    }
  }

  public async writeFile(blob: any, path: string, directory: Directory) {
    await write_blob({
      path,
      directory,
      blob,
      fast_mode: true,
      recursive: true,
      on_fallback: (err) => {
        this.logger.error({
          functionName: "writeFile",
          path,
          directory,
          error: err,
        });
      },
    });
    this.stopServingFile(path);
    return { folder: `/${directory}`, path: `/${directory}/${path}` };
  }

  public async getUri(path: string, directory: Directory = Directory.Data) {
    try {
      const res = await Filesystem.getUri({
        path,
        directory,
      });
      return res.uri;
    } catch (err) {
      this.logger.error({
        functionName: "getUri",
        path,
        directory,
        error: err,
      });
      return "";
    }
  }

  public getContentType(filePath: string, fileType: string = ""): string {
    if (filePath.endsWith(".png")) {
      return "image/png";
    }
    if (filePath.endsWith(".jpeg") || filePath.endsWith(".jpg")) {
      return "image/jpeg";
    }
    if (filePath.endsWith(".gif")) {
      return "image/gif";
    }
    if (filePath.endsWith(".webp")) {
      return "image/webp";
    }
    return fileType;
  }

  public async readFile(path: string, directory: Directory = Directory.Data) {
    try {
      const res = await Filesystem.readFile({
        path,
        directory,
      });
      const contentType = this.getContentType(
        path,
        res.data instanceof Blob ? res?.data?.type : "",
      );
      return this.base64StringToBlobConversion(res.data, contentType);
    } catch (err) {
      this.logger.error({
        functionName: "readFile",
        path,
        directory,
        error: err,
      });
      return false;
    }
  }

  public async serveFile(path: string, directory: Directory = Directory.Data) {
    if (this.objectURLService.exists(path)) {
      return this.objectURLService.get(path);
    }
    const blob = await this.readFile(path, directory);
    if (!blob) {
      return "assets/404.webp";
    }
    const url = this.objectURLService.create(path, blob);
    return Capacitor.convertFileSrc(url);
  }

  public revokeObjectUrls() {
    this.objectURLService.clear();
  }

  public async writeTempFile(
    data: string | Blob,
    name: string,
    directory: Directory = Directory.Cache,
  ) {
    const blob = await this.convertObjectUrlToFile(data);
    await this.writeFile(blob, name, directory);
    return name;
  }

  public async shareFile(
    src: string | Blob,
    name: string,
    directory: Directory = Directory.Cache,
  ) {
    try {
      const path = await this.writeTempFile(src, name, directory);
      const url = await this.getUri(path, directory);
      if (!url) {
        this.logger.error("unable to share file", { name, directory });
        return false;
      }
      await Share.share({
        url,
      });
      return true;
    } catch (err) {
      this.logger.error({
        functionName: "shareFile",
        src,
        fileName: name,
        error: err,
      });
      return false;
    }
  }
}
